import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ShopError } from '../api';
import { useCustomer } from '../auth/CustomerContext';
import { usePageMeta } from '../seo';

/**
 * Registrace zákaznického účtu.
 *
 * Účet je nepovinný – objednat jde dál i bez něj (host checkout). Účet přidá
 * jen pohodlí: přihlášení a historii objednávek na jednom místě.
 *
 * Heslo jde výhradně v těle POST (přes api.registerCustomer), nikdy do URL.
 */
export function RegisterPage() {
  usePageMeta({ title: 'Registrace | Super Krmivo', index: false });
  const { customer, loading, register } = useCustomer();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Přihlášený zákazník tu nemá co dělat – rovnou na účet.
  if (!loading && customer) return <Navigate to="/ucet" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setErrorField(null);
    try {
      await register({
        email: email.trim(),
        password,
        ...(fullName.trim() === '' ? {} : { fullName: fullName.trim() }),
        ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
      });
      navigate('/ucet');
    } catch (caught) {
      if (caught instanceof ShopError) {
        setError(caught.message);
        setErrorField(caught.field);
      } else {
        setError('Registrace se nezdařila. Zkus to prosím znovu.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="account-page narrow">
      <h1>Registrace</h1>
      <p className="muted">
        Založ si účet a měj přehled o svých objednávkách. Stačí e-mail a heslo, zbytek je
        nepovinný.
      </p>

      <form className="account-form-page" onSubmit={submit}>
        <label className={errorField === 'email' ? 'field has-error' : 'field'}>
          <span>E-mail</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        <label className={errorField === 'password' ? 'field has-error' : 'field'}>
          <span>Heslo (aspoň 12 znaků)</span>
          <input
            type="password"
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={12}
            required
          />
        </label>

        <label className="field">
          <span>Jméno a příjmení (nepovinné)</span>
          <input
            type="text"
            name="fullName"
            autoComplete="name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Telefon (nepovinné)</span>
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </label>

        {error !== null && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="button primary" disabled={submitting}>
          {submitting ? 'Zakládám účet…' : 'Vytvořit účet'}
        </button>
      </form>

      <p className="muted small">
        Máte už účet?{' '}
        <Link to="/objednavka">Sledovat objednávku bez přihlášení</Link> nebo se přihlaste v horním
        pruhu.
      </p>
    </div>
  );
}
